import { baseApi } from '@/store/api/baseApi';
import { API_URL } from '@/config/app';
import type { RootState } from '@/store';
import type {
  BulkMediaIdsPayload,
  BulkMediaStatusPayload,
  MediaImage,
  MediaListParams,
  PaginatedMediaImages,
  ReorderPayload,
  UpdateMediaPayload,
} from '@/types/media';
import { createAccessToken } from '@/utils/agent';
import { getRtkErrorMessage } from '@/utils/apiResponse';
import { isApiResponse } from '@/types/api';

function buildMediaListQuery(params: MediaListParams): string {
  const searchParams = new URLSearchParams();
  searchParams.set('page', String(params.page ?? 1));
  searchParams.set('limit', String(params.limit ?? 100));
  searchParams.set('sortBy', params.sortBy ?? 'sortOrder');
  searchParams.set('sortOrder', params.sortOrder ?? 'asc');

  if (params.status) searchParams.set('status', params.status);

  return searchParams.toString();
}

export const mediaApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getMediaImages: builder.query<PaginatedMediaImages, MediaListParams>({
      query: (params) => ({
        url: `/media?${buildMediaListQuery(params)}`,
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}(${buildMediaListQuery(queryArgs)})`,
      forceRefetch: ({ currentArg, previousArg }) =>
        buildMediaListQuery(currentArg ?? {}) !== buildMediaListQuery(previousArg ?? {}),
      providesTags: (result) =>
        result?.results
          ? [
              ...result.results.map(({ id }) => ({ type: 'Media' as const, id })),
              { type: 'Media', id: 'LIST' },
            ]
          : [{ type: 'Media', id: 'LIST' }],
    }),
    uploadMediaImages: builder.mutation<{ items: MediaImage[] }, FormData>({
      async queryFn(formData, api) {
        const { accessToken } = (api.getState() as RootState).auth;

        try {
          const response = await fetch(`${API_URL}/media/upload`, {
            method: 'POST',
            headers: {
              ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
              'x-access-token': createAccessToken(),
            },
            body: formData,
          });

          const json: unknown = await response.json();

          if (!response.ok) {
            return {
              error: {
                status: response.status,
                data: getRtkErrorMessage({ data: json }) ?? 'Image upload failed.',
              },
            };
          }

          const payload = isApiResponse<{ items: MediaImage[] }>(json)
            ? json.data
            : (json as { items: MediaImage[] });
          const message = isApiResponse<{ items: MediaImage[] }>(json) ? json.message : undefined;

          return {
            data: payload,
            ...(message ? { meta: { message } } : {}),
          };
        } catch {
          return {
            error: {
              status: 'FETCH_ERROR',
              error: 'Image upload failed.',
            },
          };
        }
      },
      invalidatesTags: [{ type: 'Media', id: 'LIST' }],
    }),
    updateMediaImage: builder.mutation<MediaImage, { id: string; body: UpdateMediaPayload }>({
      query: ({ id, body }) => ({ url: `/media/update/${id}`, method: 'POST', body }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Media', id },
        { type: 'Media', id: 'LIST' },
      ],
    }),
    deleteMediaImage: builder.mutation<void, string>({
      query: (id) => ({ url: `/media/delete/${id}`, method: 'POST' }),
      invalidatesTags: [{ type: 'Media', id: 'LIST' }],
    }),
    reorderMediaImages: builder.mutation<MediaImage[], ReorderPayload>({
      query: (body) => ({ url: '/media/reorder', method: 'POST', body }),
      invalidatesTags: [{ type: 'Media', id: 'LIST' }],
    }),
    bulkUpdateMediaStatus: builder.mutation<{ count: number }, BulkMediaStatusPayload>({
      query: (body) => ({ url: '/media/bulk-status', method: 'POST', body }),
      invalidatesTags: [{ type: 'Media', id: 'LIST' }],
    }),
    bulkDeleteMediaImages: builder.mutation<{ count: number }, BulkMediaIdsPayload>({
      query: (body) => ({ url: '/media/bulk-delete', method: 'POST', body }),
      invalidatesTags: [{ type: 'Media', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetMediaImagesQuery,
  useUploadMediaImagesMutation,
  useUpdateMediaImageMutation,
  useDeleteMediaImageMutation,
  useReorderMediaImagesMutation,
  useBulkUpdateMediaStatusMutation,
  useBulkDeleteMediaImagesMutation,
} = mediaApi;
